import { RightOutlined } from '@ant-design/icons';
import type { TenantRef } from '@serievo/contracts';
import { DatePicker, Flex, Segmented, Select, Space, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { platformColor } from '../../theme';
import type { ContextSelection } from './contextParams';

interface Props {
  tenants: readonly TenantRef[];
  selection: ContextSelection;
  onChange: (patch: ContextSelection) => void;
  loading?: boolean;
}

const ALL_SHOPS = '__all__';

function periodToDayjs(p: string | undefined): { kind: 'MONTH' | 'QUARTER'; value: Dayjs | null } {
  if (!p) return { kind: 'MONTH', value: null };
  const q = /^(\d{4})-Q([1-4])$/.exec(p);
  if (q) return { kind: 'QUARTER', value: dayjs(new Date(Number(q[1]), (Number(q[2]) - 1) * 3, 1)) };
  return { kind: 'MONTH', value: dayjs(`${p}-01`) };
}

function dayjsToPeriod(d: Dayjs, kind: 'MONTH' | 'QUARTER'): string {
  return kind === 'MONTH' ? d.format('YYYY-MM') : `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`;
}

const Step = () => <RightOutlined style={{ fontSize: 10, color: 'var(--muted)' }} aria-hidden />;

/** Thanh ngữ cảnh: Công ty → Pháp nhân → Shop → Kỳ. Mọi lựa chọn nằm trên URL. */
export function ContextBar({ tenants, selection, onChange, loading }: Props) {
  const tenant = tenants.find((t) => t.id === selection.company);
  const entity = tenant?.legalEntities.find((e) => e.id === selection.entity);
  const period = periodToDayjs(selection.period);

  return (
    <div className="context-bar" role="toolbar" aria-label="Ngữ cảnh làm việc">
      <Flex wrap gap={8} align="center">
        <Space size={4} direction="vertical">
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Công ty
          </Typography.Text>
          <Select
            aria-label="Công ty"
            loading={loading}
            value={selection.company}
            onChange={(company: string) => onChange({ company })}
            options={tenants.map((t) => ({ value: t.id, label: t.name }))}
            showSearch
            optionFilterProp="label"
            placeholder="Chọn công ty"
            popupMatchSelectWidth={false}
          />
        </Space>
        <Step />
        <Space size={4} direction="vertical">
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Pháp nhân
          </Typography.Text>
          <Select
            aria-label="Pháp nhân"
            value={selection.entity}
            onChange={(e: string) => onChange({ entity: e })}
            disabled={!tenant}
            options={(tenant?.legalEntities ?? []).map((e) => ({
              value: e.id,
              label: (
                <Space size={6}>
                  <span>{e.shortName ?? e.name}</span>
                  <Typography.Text type="secondary" className="num" style={{ fontSize: 11 }}>
                    MST {e.taxCode}
                  </Typography.Text>
                </Space>
              ),
              title: `${e.name} (MST ${e.taxCode})`,
            }))}
            placeholder="Chọn pháp nhân"
            popupMatchSelectWidth={false}
          />
        </Space>
        <Step />
        <Space size={4} direction="vertical">
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Shop
          </Typography.Text>
          <Select
            aria-label="Shop"
            value={selection.shop ?? ALL_SHOPS}
            onChange={(v: string) => onChange({ shop: v === ALL_SHOPS ? undefined : v })}
            disabled={!entity}
            options={[
              { value: ALL_SHOPS, label: `Tất cả shop (${entity?.shops.length ?? 0})` },
              ...(entity?.shops ?? []).map((s) => ({
                value: s.id,
                label: (
                  <Space size={6}>
                    <Tag
                      bordered={false}
                      style={{ color: platformColor[s.platform].c, background: platformColor[s.platform].cs, marginInlineEnd: 0 }}
                    >
                      {s.platform === 'shopee' ? 'Shopee' : 'TikTok'}
                    </Tag>
                    {s.shopName}
                  </Space>
                ),
                title: s.shopName,
              })),
            ]}
            popupMatchSelectWidth={false}
          />
        </Space>
        <Step />
        <Space size={4} direction="vertical">
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Kỳ
          </Typography.Text>
          <Space.Compact>
            <Segmented
              size="middle"
              value={period.kind}
              options={[
                { value: 'MONTH', label: 'Tháng' },
                { value: 'QUARTER', label: 'Quý' },
              ]}
              onChange={(kind) => onChange({ period: dayjsToPeriod(period.value ?? dayjs(), kind as 'MONTH' | 'QUARTER') })}
            />
            <DatePicker
              aria-label="Kỳ"
              picker={period.kind === 'MONTH' ? 'month' : 'quarter'}
              value={period.value}
              allowClear={false}
              format={(d) => (period.kind === 'MONTH' ? d.format('MM/YYYY') : `Quý ${Math.floor(d.month() / 3) + 1}/${d.year()}`)}
              onChange={(d) => d && onChange({ period: dayjsToPeriod(d, period.kind) })}
              style={{ width: 130 }}
            />
          </Space.Compact>
        </Space>
      </Flex>
    </div>
  );
}
